// Phase 38: Command Palette — date-token parser + date matcher.
//
// Extracted from CommandPalette.jsx into its own module so the pure
// helpers can be unit-tested without a JSX runtime.
//
// Date tokens supported:
//   YYYY-MM-DD  /  YYYY/MM/DD  /  YYYY.MM/DD        (kind: day)
//   YYYY-MM     /  YYYY/MM     /  YYYY.MM            (kind: month)
//   YYYY        (4-digit year)                       (kind: year)
//   YYYYMMDD    /  YYYYMM      (compact form)       (day/month)
//   MM-DD       (current year)                      (day)
//
// Multiple tokens (separated by whitespace) are AND'd: every date
// token must match the tx's occurred_on, AND every text token must
// hit the haystack. Tokens that LOOK like dates but are bogus
// (e.g. month=13) fall back to plain-text matching.

/**
 * Parse a free-text query into date tokens + remaining text tokens.
 * @param {string} query
 * @returns {{ dates: Array<{kind:'day'|'month'|'year', y:number, m?:number, d?:number}>, texts: string[] }}
 */
export function parseQueryTokens(query) {
  const raw = (query || '').trim().toLowerCase();
  if (!raw) return { dates: [], texts: [] };
  const tokens = raw.split(/\s+/).filter(Boolean);
  const dates = [];
  const texts = [];
  const normalize = (s) => s.replace(/[/.]/g, '-');
  const isValidDate = (y, m, d) => {
    if (!Number.isInteger(y) || y < 1900 || y > 2999) return false;
    if (!Number.isInteger(m) || m < 1   || m > 12)    return false;
    if (d === undefined) return true; // YYYY-MM or YYYY: no day check
    if (d < 1 || d > 31) return false;
    const probe = new Date(y, m - 1, d);
    return probe.getFullYear() === y && probe.getMonth() === m - 1 && probe.getDate() === d;
  };
  for (const tok of tokens) {
    let matched = false;
    const norm = normalize(tok);
    // YYYYMMDD / YYYYMM
    if (/^\d{8}$/.test(tok)) {
      const y = +tok.slice(0, 4), m = +tok.slice(4, 6), d = +tok.slice(6, 8);
      if (isValidDate(y, m, d)) { dates.push({ kind: 'day', y, m, d }); matched = true; }
    } else if (/^\d{6}$/.test(tok)) {
      const y = +tok.slice(0, 4), m = +tok.slice(4, 6);
      if (isValidDate(y, m)) { dates.push({ kind: 'month', y, m }); matched = true; }
    } else if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(norm)) {
      const [y, m, d] = norm.split('-').map(Number);
      if (isValidDate(y, m, d)) { dates.push({ kind: 'day', y, m, d }); matched = true; }
    } else if (/^\d{4}-\d{1,2}$/.test(norm)) {
      const [y, m] = norm.split('-').map(Number);
      if (isValidDate(y, m)) { dates.push({ kind: 'month', y, m }); matched = true; }
    } else if (/^\d{4}$/.test(tok)) {
      const y = +tok;
      if (isValidDate(y, 1)) { dates.push({ kind: 'year', y }); matched = true; }
    } else if (/^\d{1,2}-\d{1,2}$/.test(norm)) {
      // MM-DD in current year (most users typing "08-12" mean a recent date).
      const [m, d] = norm.split('-').map(Number);
      if (isValidDate(new Date().getFullYear(), m, d)) {
        dates.push({ kind: 'day', y: new Date().getFullYear(), m, d });
        matched = true;
      }
    }
    if (!matched) texts.push(tok);
  }
  return { dates, texts };
}

/**
 * Check whether a transaction's `occurred_on` (YYYY-MM-DD) matches every
 * date token. Token kinds:
 *   day   → tx date must equal (y, m, d)
 *   month → tx date must be in (y, m)
 *   year  → tx date must be in y
 * Empty `dates` array always returns true (no date filter active).
 */
export function dateMatches(occurredOn, dates) {
  if (!dates.length) return true;
  if (!occurredOn) return false;
  const [ty, tm, td] = occurredOn.split('-').map(Number);
  return dates.every(d => {
    if (d.kind === 'day')   return ty === d.y && tm === d.m && td === d.d;
    if (d.kind === 'month') return ty === d.y && tm === d.m;
    if (d.kind === 'year')  return ty === d.y;
    return true;
  });
}
